<template>
  <v-layout class="chat-layout">
    <!-- Drawer: Lista de conversaciones -->
    <v-navigation-drawer
      v-model="drawerOpen"
      :permanent="!isMobile"
      :temporary="isMobile"
      :width="isMobile ? '100%' : 380"
      :style="isMobile ? { width: '100% !important' } : {}"
    >
      <!-- Header del panel -->
      <template #prepend>
        <div class="conversations-header">
          <div class="d-flex align-center pa-3">
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">Chats</div>
              <div class="text-caption d-flex align-center">
                <span class="text-grey">{{ clinicName }}</span>

                <v-chip
                  v-if="chatStore.attentionNeededCount > 0"
                  size="x-small"
                  color="error"
                  variant="elevated"
                  class="ml-2 attention-chip"
                >
                  <v-icon size="12" class="mr-1">mdi-alert-circle</v-icon>
                  {{ chatStore.attentionNeededCount }}
                </v-chip>

                <v-icon
                  v-if="isConnected"
                  size="10"
                  color="success"
                  class="ml-2"
                >mdi-circle</v-icon>
              </div>
            </div>
            <v-menu>
              <template #activator="{ props: menuProps }">
                <v-btn icon="mdi-dots-vertical" variant="text" size="small" v-bind="menuProps" />
              </template>
              <v-list density="compact">
                <v-list-item @click="searchDialogOpen = true">
                  <template #prepend><v-icon size="small">mdi-magnify</v-icon></template>
                  <v-list-item-title>Buscar</v-list-item-title>
                </v-list-item>
                <v-divider class="my-1" />
                <v-list-item @click="toggleSounds">
                  <template #prepend>
                    <v-icon size="small" :color="chatStore.soundsEnabled ? 'success' : 'grey'">
                      {{ chatStore.soundsEnabled ? 'mdi-volume-high' : 'mdi-volume-off' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    {{ chatStore.soundsEnabled ? 'Silenciar' : 'Activar sonido' }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- Busqueda -->
          <div class="px-3 pb-2">
            <v-text-field
              v-model="searchQuery"
              placeholder="Buscar..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </div>
        </div>
      </template>

      <!-- Lista de conversaciones -->
      <ConversationList
        :conversations="filteredConversations"
        :loading="chatStore.conversationsLoading"
        :selected-conversation="chatStore.currentConversation"
        @select="selectConversation"
        @refresh="refreshConversations"
      />
    </v-navigation-drawer>

    <!-- Panel derecho: Ventana de chat -->
    <v-main class="chat-main">
      <ChatWindow
        v-if="chatStore.currentConversation"
        :conversation="chatStore.currentConversation"
        :messages="chatStore.currentMessages"
        :loading="chatStore.messagesLoading"
        :sending="chatStore.sendingMessage"
        :show-back="isMobile"
        :has-more="chatStore.hasMoreMessages[chatStore.currentConversation?.id] || false"
        :loading-older="chatStore.loadingOlderMessages"
        @send-message="sendMessage"
        @close="closeChat"
        @load-older="loadOlderMessages"
      />

      <!-- Estado vacio (solo desktop) -->
      <div v-else class="empty-chat-state">
        <div class="text-center">
          <v-icon size="80" color="grey-lighten-3">mdi-chat-outline</v-icon>
          <div class="text-h6 text-grey mt-3 mb-1">Selecciona una conversacion</div>
          <div class="text-caption text-grey">Solo puedes responder conversaciones iniciadas por pacientes</div>
        </div>
      </div>
    </v-main>

    <!-- Dialog busqueda -->
    <v-dialog v-model="searchDialogOpen" max-width="500">
      <v-card>
        <v-card-title>Buscar Conversaciones</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="advancedSearchQuery"
            label="Nombre o telefono"
            variant="outlined"
            prepend-inner-icon="mdi-account-search"
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="searchDialogOpen = false">Cancelar</v-btn>
          <v-btn color="primary" @click="performAdvancedSearch">Buscar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useDisplay } from 'vuetify'
import { useChatStore } from '@/stores/chat'
import { useClinicsStore } from '@/stores/clinics'
import ConversationList from './ConversationList.vue'
import ChatWindow from './ChatWindow.vue'

const props = defineProps({
  clinicId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['error'])

const chatStore = useChatStore()
const clinicsStore = useClinicsStore()
const { smAndDown } = useDisplay()

const isMobile = computed(() => smAndDown.value)

// Drawer starts open (desktop: permanent, mobile: open by default showing list)
const drawerOpen = ref(true)

// State
const searchQuery = ref('')
const searchDialogOpen = ref(false)
const advancedSearchQuery = ref('')

// Computed
const clinicName = computed(() => {
  if (!props.clinicId) return ''
  const clinic = clinicsStore.getClinicById(props.clinicId)
  return clinic?.name || clinic?.clinic_name || `Clinica ${props.clinicId}`
})

const isConnected = computed(() => chatStore.subscription !== null)

const filteredConversations = computed(() => {
  let conversations = chatStore.conversationsForCurrentClinic

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    conversations = conversations.filter(conv =>
      conv.patient_name?.toLowerCase().includes(q) ||
      conv.patient_phone?.includes(q)
    )
  }

  return [...conversations].sort((a, b) => {
    if (a.needs_attention && !b.needs_attention) return -1
    if (!a.needs_attention && b.needs_attention) return 1
    return new Date(b.last_message_time) - new Date(a.last_message_time)
  })
})

// Methods
const selectConversation = (conversation) => {
  chatStore.selectConversation(conversation)
  // On mobile, close drawer to show chat
  if (isMobile.value) {
    drawerOpen.value = false
  }
}

const sendMessage = async (content) => {
  await chatStore.sendMessage(content)
}

const closeChat = () => {
  chatStore.selectConversation(null)
  // On mobile, reopen drawer to show list
  if (isMobile.value) {
    drawerOpen.value = true
  }
}

const loadOlderMessages = () => {
  if (chatStore.currentConversation) {
    chatStore.loadOlderMessages(chatStore.currentConversation.id)
  }
}

const refreshConversations = () => {
  chatStore.loadConversations()
}

const performAdvancedSearch = () => {
  searchQuery.value = advancedSearchQuery.value
  searchDialogOpen.value = false
}

const toggleSounds = () => {
  chatStore.setSoundsEnabled(!chatStore.soundsEnabled)
}

// When conversation is cleared externally, reopen drawer on mobile
watch(() => chatStore.currentConversation, (val) => {
  if (!val && isMobile.value) {
    drawerOpen.value = true
  }
})

// Lifecycle
onMounted(async () => {
  await chatStore.requestNotificationPermission()
  await chatStore.initializeAudio()

  if (props.clinicId) {
    chatStore.setCurrentClinic(props.clinicId)
  }

  await chatStore.loadConversations()
  chatStore.subscribeToMessages()
})

onUnmounted(() => {
  chatStore.unsubscribeFromMessages()
})
</script>

<style scoped>
.chat-layout {
  display: flex;
  height: 100%;
  overflow: hidden;
}

.conversations-header {
  background: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.chat-main {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
}

.empty-chat-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attention-chip {
  animation: pulse 2s infinite;
  font-weight: bold !important;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
}
</style>
